import { ReactNode, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth, UserRole } from '../../hooks/useAuth';
import { unauthorizedRoleRedirect } from '../../lib/authRoles';
import { supabase } from '../../lib/supabase';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, loading, isAuthenticated } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const wasAuthorizedRef = useRef(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    setSigningOut(true);
    try { await supabase.auth.signOut(); } catch { /* best effort */ }
    navigate('/entrar', { replace: true });
  };

  if (loading) {
    if (wasAuthorizedRef.current) return <>{children}</>;
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B35] mb-4"></div>
          <p className="text-gray-400">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-white mb-3">Acesso não autorizado</h2>
          <p className="text-gray-400 text-sm mb-6">
            A sessão não possui um perfil ativo ligado pela identidade canônica.
          </p>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="px-8 py-3 bg-[#FF6B35] text-white font-bold rounded-xl disabled:opacity-50"
          >
            {signingOut ? 'Saindo...' : 'Sair e entrar novamente'}
          </button>
        </div>
      </div>
    );
  }

  if (!allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(user.role)) {
    wasAuthorizedRef.current = true;
    return <>{children}</>;
  }

  wasAuthorizedRef.current = false;
  return <Navigate to={unauthorizedRoleRedirect(user.role) || '/'} replace />;
}
